import { useShallow } from "zustand/react/shallow";
import { audioStore } from "./audioStore";


export const useAudioControls = () =>
  audioStore(
    useShallow((s) => ({
      /** Plays a specific song while keeping the player either in current mode */
      playSong: s.playSong,
      pause: s.pause,
      resume: s.resume,
      next: s.next,
      prev: s.prev,
      seek: s.seek,
      skip: s.skip,
      toggleLoop: s.toggleLoop,
      toggleShuffle: s.toggleShuffle,
      /** Opens the big player */
      open: s.open,
      /** Collapses the player to mini mode */
      close: s.close,
    })),
  );

export const useAudioCurrentState = (songId: string) =>
  audioStore(
    useShallow((s) => ({
      isCurrent: s.currentSong?.id === songId,
      isCurrentPlaying: s.currentSong?.id === songId && s.isPlaying,
    })),
  );

export const useAudioMiniPlayerState = () =>
  audioStore(
    useShallow((s) => ({
      currentSong: s.currentSong,
      isPlaying: s.isPlaying,
      duration: s.duration,
      playlist: s.playlist,
      loop: s.loop,
      shuffle: s.shuffle,
      bigPlayerOpen: s.bigPlayerOpen,
      setBigPlayerOpen: s.setBigPlayerOpen,
      isCasting: s.isCasting,
      castingDeviceName: s.castingDeviceName,
    })),
  );

export const useAudioSeekbarState = () =>
  audioStore(
    useShallow((s) => ({
      currentTime: s.currentTime,
      duration: s.duration,
    })),
  );

export const useAudioPrevNextState = () =>
  audioStore(
    useShallow((s) => ({
      prevSong: (() => {
        const len = s.playlist?.length ?? 0;
        const idx = typeof s.currentIndex === "number" ? s.currentIndex : -1;
        if (len === 0 || idx < 0) return null;
        if (idx > 0) return s.playlist[idx - 1] ?? null;
        if (s.loop && len > 0) return s.playlist[len - 1] ?? null;
        return null;
      })(),
      nextSong: (() => {
        const len = s.playlist?.length ?? 0;
        const idx = typeof s.currentIndex === "number" ? s.currentIndex : -1;
        if (len === 0 || idx < 0) return null;
        if (idx < len - 1) return s.playlist[idx + 1] ?? null;
        if (s.loop && len > 0) return s.playlist[0] ?? null;
        return null;
      })(),
      loop: s.loop,
      shuffle: s.shuffle,
    })),
  );

export const useAudioOpen = () =>
  audioStore(
    useShallow((s) => ({
      bigPlayerOpen: s.bigPlayerOpen,
      miniPlayerOpen: Boolean(s.currentSong && !s.bigPlayerOpen),
    })),
  );

export const useCurrentSongActions = () =>
  audioStore((s) => s.currentSongActions);
